import type {
  ContactPlatformProfileSummaryDto,
  ContactState,
  ConversationStatusName,
  PlatformTypeName,
} from "@multi-chat/shared";
import { deriveReplyWindow, type ReplyWindow } from "../conversation-header-timing.js";

export type ProviderIdentityField = "platformDisplayName" | "phone" | "username";
export type CrmContactField = "firstName" | "lastName";

/** `value: null` renders as an empty placeholder; the field applies but the provider sent nothing. */
export type DetailsLabeledValue<Id extends string = string> = {
  id: Id;
  value: string | null;
};

export type ConversationDetailsModel = {
  secondaryIdentity: string | null;
  /** Provider-owned identity from ContactPlatformProfile, shown whether or not the sender is saved. */
  providerRows: DetailsLabeledValue<ProviderIdentityField>[];
  /** CRM Contact fields; null unless the profile is linked to an active Contact. */
  crmRows: DetailsLabeledValue<CrmContactField>[] | null;
  contactState: ContactState;
  conversationStatus: ConversationStatusName;
  closedAt: string | null;
  /** 24h reply window for every channel; null while the conversation details are loading. */
  replyWindow: Exclude<ReplyWindow, { kind: "NONE" }> | null;
};

function differsFromTitle(value: string, title: string): boolean {
  return value.trim().toLowerCase() !== title.trim().toLowerCase();
}

/**
 * Identity fields each provider can populate on ContactPlatformProfile. WhatsApp sends a
 * profile name and wa_id phone; Instagram a name and username; Messenger only a name
 * (first/last are combined before storage and must not be split back).
 */
export function providerIdentityFields(platformType: PlatformTypeName): ProviderIdentityField[] {
  switch (platformType) {
    case "WHATSAPP":
      return ["platformDisplayName", "phone"];
    case "INSTAGRAM":
      return ["platformDisplayName", "username"];
    case "FACEBOOK":
      return ["platformDisplayName"];
    default: {
      const _exhaustive: never = platformType;
      return _exhaustive;
    }
  }
}

const PROVIDER_FIELD_ORDER: ProviderIdentityField[] = ["platformDisplayName", "username", "phone"];

/**
 * Builds Details fields from real Conversation / profile data. Provider identity is listed
 * for every sender (saved or not); fields the provider supports but did not send are kept
 * as empty values. CRM names come only from a linked active Contact.
 */
export function buildConversationDetailsModel(input: {
  title: string;
  platformType: PlatformTypeName;
  status: ConversationStatusName;
  closedAt: string | null;
  profile: ContactPlatformProfileSummaryDto;
  /** `undefined` = details not loaded yet (omit the reply-window section). */
  customerServiceWindowExpiresAt: string | null | undefined;
  lastInboundCustomerMessageAt?: string | null;
  /** Backend `canSendFreeformText` for Messenger / Instagram; `undefined` while not loaded. */
  metaWindowOpen?: boolean;
  nowMs?: number;
}): ConversationDetailsModel {
  const { title, platformType, status, closedAt, profile } = input;
  const phone = profile.normalizedPhoneNumber?.trim() || null;
  const username = profile.platformUsername?.trim() || null;
  const platformDisplayName = profile.platformDisplayName?.trim() || null;

  let secondaryIdentity: string | null = null;
  if (phone && differsFromTitle(phone, title)) {
    secondaryIdentity = phone;
  } else if (username && differsFromTitle(username, title)) {
    secondaryIdentity = username;
  }

  const providerValues: Record<ProviderIdentityField, string | null> = {
    platformDisplayName,
    username,
    phone,
  };
  const applicable = new Set(providerIdentityFields(platformType));
  const providerRows = PROVIDER_FIELD_ORDER.filter(
    (id) => applicable.has(id) || providerValues[id] != null,
  ).map((id) => ({ id, value: providerValues[id] }));

  const crmRows: ConversationDetailsModel["crmRows"] =
    profile.contactState === "ACTIVE" && profile.contact
      ? [
          { id: "firstName", value: profile.contact.firstName?.trim() || null },
          { id: "lastName", value: profile.contact.lastName?.trim() || null },
        ]
      : null;

  const windowLoaded =
    platformType === "WHATSAPP"
      ? input.customerServiceWindowExpiresAt !== undefined
      : input.metaWindowOpen !== undefined;
  let replyWindow: ConversationDetailsModel["replyWindow"] = null;
  if (windowLoaded) {
    const derived = deriveReplyWindow({
      platformType,
      customerServiceWindowExpiresAt: input.customerServiceWindowExpiresAt ?? null,
      lastInboundCustomerMessageAt: input.lastInboundCustomerMessageAt ?? null,
      nowMs: input.nowMs ?? Date.now(),
      metaWindowClosed: input.metaWindowOpen === false,
    });
    // No customer message yet: the API treats the window as closed for every channel.
    replyWindow =
      derived.kind === "NONE"
        ? {
            kind: "CLOSED",
            expiresAtMs: null,
            lastInboundAtMs: null,
            templateRequired: platformType === "WHATSAPP",
          }
        : derived;
  }

  return {
    secondaryIdentity,
    providerRows,
    crmRows,
    contactState: profile.contactState,
    conversationStatus: status,
    closedAt,
    replyWindow,
  };
}
